import * as Accordion from "@radix-ui/react-accordion";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";

const carouselImages = [
	{ src: "/img/customization_server.webp", caption: "In your server" },
	{ src: "/img/customization_commands.webp", caption: "Slash command menu" },
	{ src: "/img/customization_dashboard.webp", caption: "Web dashboard" },
	{ src: "/img/customization_dm.webp", caption: "Direct messages" },
];

function CustomBrandingAnswer() {
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
	const [selectedIndex, setSelectedIndex] = useState(0);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
	const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on("select", onSelect);
		return () => {
			emblaApi.off("select", onSelect);
		};
	}, [emblaApi, onSelect]);

	return (
		<>
			<p>
				With{" "}
				<span className="font-semibold text-slate-100">sesh Pro</span>,
				you can give sesh a custom identity in your server:
			</p>
			<ul className="mt-2 list-inside list-disc space-y-1 text-slate-300">
				<li>Custom bot name (up to 32 characters)</li>
				<li>Custom bot avatar</li>
				<li>Custom branding in the web dashboard</li>
			</ul>

			<p className="mt-4 text-slate-300">
				See the screenshots below to see how your custom icon and name
				will appear in Discord and on the dashboard:
			</p>

			{/* Image Carousel */}
			<div className="mt-4">
				<div className="overflow-hidden rounded-lg" ref={emblaRef}>
					<div className="flex touch-pan-y">
						{carouselImages.map((image, index) => (
							<div
								key={index}
								className="min-w-0 flex-[0_0_100%]"
							>
								<img
									src={image.src}
									alt={image.caption}
									className="block h-auto max-h-[400px] w-full cursor-pointer rounded-lg border border-white/10 object-contain transition-opacity hover:opacity-90"
									onClick={() => window.open(image.src, "_blank")}
								/>
							</div>
						))}
					</div>
				</div>

				<p className="mt-2 text-center text-sm font-semibold text-white">
					{carouselImages[selectedIndex].caption}
				</p>

				<div className="mt-3 flex items-center justify-center gap-4">
					<button
						className="rounded border border-white/20 bg-white/10 px-3 py-1.5 text-sm text-white transition-all hover:border-sesh-primary/50 hover:bg-white/20"
						onClick={scrollPrev}
						aria-label="Previous slide"
					>
						<i className="fas fa-chevron-left mr-1"></i> Prev
					</button>
					<div className="flex items-center gap-2">
						{carouselImages.map((_, index) => (
							<button
								key={index}
								className={`h-2 w-2 rounded-full border-none p-0 transition-colors ${
									index === selectedIndex
										? "bg-sesh-primary"
										: "cursor-pointer bg-white/30 hover:bg-white/50"
								}`}
								onClick={() => scrollTo(index)}
								aria-label={`Go to slide ${index + 1}`}
							/>
						))}
					</div>
					<button
						className="rounded border border-white/20 bg-white/10 px-3 py-1.5 text-sm text-white transition-all hover:border-sesh-primary/50 hover:bg-white/20"
						onClick={scrollNext}
						aria-label="Next slide"
					>
						Next <i className="fas fa-chevron-right ml-1"></i>
					</button>
				</div>
			</div>

			{/* Note */}
			<div className="mt-4 rounded-lg border border-slate-600 bg-slate-800/50 px-4 py-3">
				<div className="flex items-center gap-2">
					<i className="fas fa-info-circle text-sm text-slate-400"></i>
					<p className="text-sm text-slate-300">
						<span className="font-medium text-slate-200">Note:</span>{" "}
						The "sesh" bot name will still appear in a few places: in
						Direct Messages (your custom name is shown with "AKA"), in
						the slash command menu category header, in bot status, and
						in some places on the web dashboard and manual.
					</p>
				</div>
			</div>

			<p className="mt-4">
				<a
					href="/manual#bot_customization"
					className="inline-flex items-center gap-1.5 text-sesh-primary hover:underline"
				>
					<span>Learn more in the manual</span>
					<i className="fas fa-arrow-right text-xs"></i>
				</a>
			</p>
		</>
	);
}

interface FaqItem {
	question: string;
	answer: React.ReactNode;
	id: string;
}

const faqItems: FaqItem[] = [
	{
		id: "transferring",
		question: "Can I transfer my premium subscription between servers?",
		answer: (
			<>
				<p>
					<span className="font-semibold text-slate-100">
						Purchases made here (Premium, Premium Bundle, sesh Pro):
					</span>{" "}
					Yes, you can transfer these subscriptions freely between any
					servers you manage. Visit the{" "}
					<a
						href="/dashboard/premium"
						className="text-sesh-primary hover:underline"
					>
						Premium dashboard
					</a>{" "}
					to manage your active servers.
				</p>
				<p className="mt-3">
					<span className="font-semibold text-slate-100">
						Discord in-app purchases:
					</span>{" "}
					These are tied to the server where they were purchased and
					cannot be transferred. If you need flexibility, we recommend
					purchasing here instead.
				</p>
			</>
		),
	},
	{
		id: "commercial",
		question: "Do I need sesh Pro for commercial use?",
		answer: (
			<>
				<p>
					Yes. Commercial use of sesh requires a{" "}
					<span className="font-semibold text-slate-100">
						sesh Pro
					</span>{" "}
					license. This includes:
				</p>
				<ul className="mt-2 list-inside list-disc space-y-1 text-slate-300">
					<li>Servers that generate revenue directly or indirectly</li>
					<li>Servers that charge members for access</li>
					<li>Servers operated by or on behalf of a business</li>
					<li>Creator/influencer communities with monetization</li>
				</ul>
				<p className="mt-3">
					Not sure if you need Pro?{" "}
					<a
						href="mailto:contact@tunks.games"
						className="text-sesh-primary hover:underline"
					>
						Contact us
					</a>{" "}
					and we'll help figure it out.
				</p>
			</>
		),
	},
	{
		id: "upgrade-pricing",
		question: "How does upgrade pricing work?",
		answer: (
			<>
				<p>
					<span className="font-semibold text-slate-100">
						Subscriptions (monthly/yearly):
					</span>{" "}
					Upgrade your existing subscription in-place with prorated
					pricing. You only pay the difference.
				</p>
				<p className="mt-3">
					<span className="font-semibold text-slate-100">
						Lifetime purchases:
					</span>{" "}
					Get a $149.99 discount on sesh Pro (the value of your lifetime
					Premium).
				</p>
				<p className="mt-3">
					<span className="font-semibold text-slate-100">
						Bundles:
					</span>{" "}
					You can upgrade some or all of your bundle servers to Pro.
					For example, upgrade 1 server to Pro while keeping 2 on
					Premium.
				</p>
			</>
		),
	},
	{
		id: "custom-branding",
		question: "What is custom branding?",
		answer: <CustomBrandingAnswer />,
	},
];

export function PremiumFaq() {
	return (
		<div className="mx-auto max-w-4xl px-4 py-16" id="faq">
			{/* Header */}
			<div className="mb-10 text-center">
				<div className="mb-3 inline-flex items-center gap-2 rounded-full border border-sesh-primary/30 bg-sesh-primary/10 px-4 py-1.5">
					<i className="fas fa-question-circle text-sm text-sesh-primary"></i>
					<span className="text-sm font-medium text-sesh-primary">
						Common Questions
					</span>
				</div>
				<h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
					Frequently Asked Questions
				</h2>
			</div>

			{/* FAQ Accordion */}
			<Accordion.Root
				type="multiple"
				className="overflow-hidden rounded-xl border border-slate-700 bg-slate-800/30 shadow-lg shadow-black/20"
			>
				{faqItems.map((item) => (
					<Accordion.Item
						key={item.id}
						value={item.id}
						className="group border-b border-slate-700/50 last:border-b-0"
					>
						<Accordion.Header>
							<Accordion.Trigger className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors duration-200 hover:bg-slate-800/40 data-[state=open]:bg-slate-800/40">
								<span className="text-base font-medium text-slate-100 transition-colors duration-200 group-data-[state=open]:text-sesh-primary sm:text-lg">
									{item.question}
								</span>
								<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-700/50 text-slate-400 transition-all duration-300 group-hover:bg-slate-700 group-data-[state=open]:bg-sesh-primary/20 group-data-[state=open]:text-sesh-primary">
									<i className="fas fa-chevron-down text-sm transition-transform duration-300 group-data-[state=open]:rotate-180"></i>
								</div>
							</Accordion.Trigger>
						</Accordion.Header>
						<Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
							<div className="px-6 pt-2 pb-6 text-sm leading-relaxed text-slate-300 sm:text-base">
								{item.answer}
							</div>
						</Accordion.Content>
					</Accordion.Item>
				))}
			</Accordion.Root>

			{/* Footer */}
			<div className="mt-8 text-center">
				<p className="text-sm text-slate-400">
					Still have questions?{" "}
					<a
						href="mailto:contact@tunks.games"
						className="text-sesh-primary hover:underline"
					>
						Reach out directly
					</a>{" "}
					or ask in our{" "}
					<a
						href="https://discord.gg/RuTcRzm"
						target="_blank"
						rel="noopener noreferrer"
						className="text-sesh-primary hover:underline"
					>
						Discord server
					</a>
					.
				</p>
			</div>
		</div>
	);
}
